import { css, html, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { EntityContent } from 'src/content/entity-content.js';
import '../../library/fields/index.js';
import '../../library/elements/d-section.js';
import '../../library/elements/d-action.js';
import '../../library/elements/d-wrap.js';
import '../../library/editors/index.js';
import '../../library/lists/d-list-section-attachment.js';
import '../../library/editors/components/d-edit-classification.js';
import '../../pages/staffing-page/d-edit-work-weeks';
import './d-slide-playlist-editor';
import './d-custom-slide';
import './d-news-slide-fhi';
import './d-news-slide-nrk';
import './d-weather-slide';
import './d-employees-slide';
import './d-slideshow';
import { customElement, property, query, state } from 'lit/decorators.js';
import { isEmptyOrInvalidString } from 'src/utilities/text';
import type { UpdateSectionItem } from '../update-section-item';
import type { ListDefinition } from '../list-definition';
import type { ScreenViewEditItem } from './screen-view-edit-item';
import type { ScreenView } from './screen-view';
import type { ScreenMediaUploadPayload } from './screen-media-upload-payload';
import type { SectionField } from '../../library/components/section-field';
import type { WorkScheduleWeek } from '@trinnvis/dabih-api-client';
import type { ActionInput } from 'src/library/elements/action-input';
import type { Slide } from 'src/content/screens/slide-element';
import { displayAlert } from 'src/utilities/display-alert';
import { DPromoDialog } from 'src/library/promo/d-promo-dialog';
import type { PromoDialogResult } from 'src/library/promo/promo-dialog-result';
import { promo } from 'src/store/selectors/promo';
import { features } from 'src/store/selectors/features/features';
import { resolveAssetUrl } from './resolve-asset-url';
import type { AlertDialogResult } from 'src/library/components/alert-dialog-result';
import { AlertDialog } from 'src/library/components/alert-dialog';
import { updateScreenDraft, deleteScreenDraft, sendScreenCommand } from 'src/store/signage/signage-api';
import type { ScreenCommand, ScreenCommandResult } from 'src/store/signage/screen-command';
import type { SlideNotificationContent } from 'src/layout/parts/slide-notification-content';
import { t } from 'src/i18n';
import type { DSlideshow } from 'src/content/screens/d-slideshow';
import { DScreenNetworkConfigDialog } from './network-config/d-screen-network-config-dialog';

const PREVIEW_API_ORIGIN = 'https://api.trinnvis.io';
const DRAFT_DEBOUNCE_MS = 300;

/**
 *
 */
@customElement('d-screen-view')
export class DScreenView extends EntityContent<ScreenView, ScreenViewEditItem> {
  static readonly styles = EntityContent.styles.concat(css`
    .status {
      padding: 12px 0;
    }
    .edit-preview-label {
      z-index: 2;
    }
    .image-container {
      width: 100%;
      aspect-ratio: 16 / 9;
      box-shadow: none;
      border: 1px solid silver;
      border-radius: 12px;
      overflow: hidden;
      background: black;
    }
    .image-container iframe {
      display: block;
      width: 100%;
      height: 100%;
      border: 0;
    }
    .image-container img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .image-container div {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      background: var(--themeColor);
      color: white;
      font-size: 18px;
    }
    .editor {
      padding: 12px 0;
    }
    .editor d-view-text {
      margin-bottom: 10px;
    }
    .playlist {
      padding: 12px 0;
    }
    .playlist d-label {
      margin-bottom: 10px;
    }
    .playlist-content {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }
    .playlist-content > * {
      width: 170px;
      aspect-ratio: 16/9;
      border: 1px solid silver;
      border-radius: 6px;
      background: white;
      box-sizing: border-box;
      overflow: hidden;
    }
    d-slideshow {
      margin-top: 6px;
    }
    d-edit-work-weeks {
      border-top: 1px solid var(--borderColor);
      padding-top: 12px;
    }
    .remote-control {
      padding: 6px 0;
      /* d-action[plain] har 8px venstre-padding på sin span – nullstill så
         tekstene flukter med innholdet over (Visningstid). */
      margin-left: -8px;
    }
  `);
  @property({ type: String })
  entityType = 'screens';
  @property({ type: Boolean })
  helpOpened = false;
  @query('d-slideshow')
  private slideshowElm?: DSlideshow | null;
  deleteWarning = '';

  /** Kommandoen som venter på svar fra serveren, eller null. Brukes til å låse
   * knappene mens det blokkerende kallet pågår (vi har ingen spinner). */
  @state()
  private commandInFlight: ScreenCommand | null = null;

  @query('#preview-iframe')
  private previewIframe?: HTMLIFrameElement;

  private draftDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  async onDelete() {
    if (!this.entityView.deletable) {
      return;
    }
    const result: AlertDialogResult = await AlertDialog.open({
      title: t('screens.deleteTitle'),
      content: t('screens.deleteConfirm'),
    });
    if (result.action !== 'ok') return;
    super.onDelete();
  }

  protected get specialEditLabel() {
    return t('screens.specialEditLabel');
  }

  protected get specialEditSublabel() {
    return t('screens.specialEditSublabel');
  }

  renderRevisionItems() {
    return html``;
  }

  renderSlide(item: Slide) {
    if (item.type === 'custom') {
      return html` <d-custom-slide
        .slide=${item}
        .assetBase=${this.entityView.assetBase}
        .library=${this.entityView.library}
      ></d-custom-slide>`;
    }
    if (item.type === 'news') {
      return html` <d-news-slide-nrk .slide=${item} .feed=${this.entityView.newsFeed}></d-news-slide-nrk>`;
    }
    if (item.type === 'fhi') {
      return html` <d-news-slide-fhi .slide=${item} .feed=${this.entityView.newsFeedFhi}></d-news-slide-fhi>`;
    }
    if (item.type === 'weather') {
      return html`<d-weather-slide .slide=${item} .assetBase=${this.entityView.assetBase}></d-weather-slide>`;
    }
    if (item.type === 'employees') {
      return html`<d-employees-slide
        .slide=${item}
        .employees=${this.entityView.employeesPresenceStatus}
      ></d-employees-slide>`;
    }
    if (item.type === 'image') {
      const url = resolveAssetUrl(item.media, this.entityView.assetBase);
      return html`<img src="${url}" />`;
    }
    return nothing;
  }

  renderViewMode() {
    const playlist = this.entityView.playlist;
    return html`
      ${this.renderOrderStatus()}
      ${playlist.length > 0
        ? html`
            <div class="image-container">${this.renderLivePreview(playlist[0], false)}</div>
            <div class="playlist">
              <d-label label=${t('screens.contentLabel')}></d-label>
              <div class="playlist-content">${playlist.map((item) => html` ${this.renderSlide(item)} `)}</div>
            </div>
            <d-view-text
              system-content
              inline-label
              label=${t('screens.displayTimeLabel')}
              .value=${this.entityView.scheduleDescription}
            ></d-view-text>
            ${this.renderRemoteControl()}
          `
        : nothing}
    `;
  }

  /**
   * Live preview av skjermen via samme runtime som faktiske players bruker.
   * `previewUrl` er en server-token-URL som finnes også før skjermen er
   * bestilt. I view-mode poller player-runtimen `/settings` hvert 30 s.
   * I edit-mode bruker vi `?mode=draft` så renderen poller `/settings?
   * draft=true` hvert 60 s (WAF-vennlig) og lytter på `postMessage` for ~0 ms latency.
   *
   * Faller tilbake til første-slide-render hvis `previewUrl` mangler.
   */
  private renderLivePreview(firstSlide: Slide | undefined, draft: boolean) {
    if (!this.entityView.previewUrl) {
      return firstSlide ? this.renderSlide(firstSlide) : nothing;
    }
    const query = draft ? '?mode=draft' : '';
    const fullUrl = `${PREVIEW_API_ORIGIN}${this.entityView.previewUrl}${query}`;
    return html`
      <iframe
        id="preview-iframe"
        src=${fullUrl}
        title=${t('screens.livePreviewTitle')}
        loading="lazy"
        sandbox="allow-scripts allow-same-origin"
      ></iframe>
    `;
  }

  editHeaderActions() {
    return [
      {
        name: t('screens.cancel'),
        action: 'cancel',
      },
      {
        name: this.entityView.orderStatus === 'connected' ? t('screens.publish') : t('screens.done'),
        action: 'done',
        disabled: this.doneDisabled(),
      },
    ];
  }

  mainHeaderMoreActions(): ActionInput[] {
    const actions: ActionInput[] = [];
    if (this.internalMode === 'edit') return actions;
    if (this.entityView.orderStatus === 'none') {
      actions.push({
        name: t('screens.deletePresentation'),
        action: 'delete',
        classes: 'delete',
      });
    }
    if (this.entityView.orderStatus === 'connected') {
      actions.push({
        name: t('screens.networkConfig.action'),
        action: 'configure-network',
      });
    }
    return actions;
  }

  protected override handleAction(action: string) {
    if (action === 'configure-network') {
      void DScreenNetworkConfigDialog.open({
        screenName: this.entityView.name,
        deviceCode: this.entityView.playerCode,
      });
      return;
    }
    super.handleAction(action);
  }

  async initializeEditItem() {
    this.editItem = {
      name: this.entityView.name,
      workWeek: this.entityView.schedule,
      playlist: [...this.entityView.playlist],
    };
  }

  protected async firstUpdated(changedProperties: PropertyValues) {
    super.firstUpdated?.(changedProperties);
    // One-shot: åpner nyopprettet skjerm direkte i edit-mode. Flagget settes
    // i d-organization-page-content.onAddScreen før navigasjon.
    const pending = sessionStorage.getItem('signage-edit-on-mount');
    if (pending && pending === String(this.entityView?.uuid ?? '')) {
      sessionStorage.removeItem('signage-edit-on-mount');
      await this.onEditModeOn();
    }
  }

  doneDisabled(): boolean {
    return isEmptyOrInvalidString(this.editItem?.name);
  }

  renderEditItem(item: ScreenViewEditItem): TemplateResult<1> {
    const workWeeks: WorkScheduleWeek[] = [item.workWeek];
    return html`
      ${this.entityView.orderStatus === 'none' ? html` ${this.renderEmptyInfo('edit')} ` : nothing}
      ${this.renderFieldSection('screens', this.editFields(), item)}
      ${item.playlist.length
        ? html`
            <d-label class="edit-preview-label" field="screens_editPreview"></d-label>
            <d-slideshow
              .playlist=${item.playlist}
              .library=${this.entityView.library}
              .assetBase=${this.entityView.assetBase}
              .newsFeed=${this.entityView.newsFeed}
              .newsFeedFhi=${this.entityView.newsFeedFhi}
              .employeesPresenceStatus=${this.entityView.employeesPresenceStatus}
            ></d-slideshow>
          `
        : nothing}
      <div class="editor">
        ${item.playlist.length
          ? html`
              <d-view-text
                system-content
                wrap-label
                label=${t('screens.contentLabel')}
                value=${t('screens.editPlaylistHint')}
              ></d-view-text>
            `
          : nothing}
        <d-slide-playlist-editor
          .orderStatus=${this.entityView.orderStatus}
          .playlist=${item.playlist}
          .templates=${this.entityView.templates}
          .suggestionsSelected=${this.entityView.suggestionsSelected}
          .suggestionsCategorized=${this.entityView.suggestionsCategorized}
          .newsFeed=${this.entityView.newsFeed}
          .newsFeedFhi=${this.entityView.newsFeedFhi}
          .media=${this.entityView.media}
          .employeesPresenceStatus=${this.entityView.employeesPresenceStatus}
          .library=${this.entityView.library}
          .screenId=${Number(this.entityView.uuid)}
          .organizationId=${Number(this.entityView.organizationId)}
          .previewUrl=${this.entityView.previewUrl ?? ''}
          .assetBase=${this.entityView.assetBase}
          @playlist-changed=${this.onPlaylistChanged}
          @upload-presentation=${this.onUploadPresentation}
          @request-order=${this.handleOrder}
        ></d-slide-playlist-editor>
      </div>
      <d-edit-work-weeks
        class="fullWidth"
        label=${t('screens.displayTimeLabel')}
        .workWeeks=${workWeeks}
        .workHoursType=${'custom'}
        @work-weeks-changed=${this.onWorkWeeksChanged}
      ></d-edit-work-weeks>
    `;
  }

  protected async saveEditItem(item: ScreenViewEditItem): Promise<ScreenViewEditItem | undefined> {
    // Player synker bare filer som ligger i `assets[]`. `media` er en kanonisk
    // referanse for å rendre slide-en — den må også speiles inn i `assets`
    // ellers 404'er filen på enheten. Se signage/DESIGN.md og dabih-api PlayerModels.kt.
    const playlist: Slide[] = item.playlist.map((slide) => {
      if (slide.type === 'custom') {
        const ref = slide.media ?? '';
        return {
          ...slide,
          media: ref,
          assets: ref ? [ref] : [],
        };
      }
      const media = typeof slide.media === 'string' ? slide.media.trim() : '';
      if (!media || /^https?:\/\//i.test(media)) {
        return slide;
      }
      const existing = Array.isArray(slide.assets) ? slide.assets : [];
      if (existing.includes(media)) {
        return slide;
      }
      return { ...slide, assets: [media, ...existing] };
    });
    // Bestillingsstatus ligger ikke lenger i navnet (#13126) – lagre navnet rått.
    return { ...item, name: item.name, playlist };
  }

  onPlaylistChanged(e: CustomEvent<{ playlist: Slide[]; slideIdForPreview: string }>) {
    e.stopPropagation();
    if (this.editItem !== undefined) {
      this.editItem = {
        ...this.editItem,
        playlist: e.detail.playlist,
      };
      if (e.detail.slideIdForPreview && this.slideshowElm) {
        const index = this.editItem.playlist.findIndex((slide) => slide.id === e.detail.slideIdForPreview);
        this.slideshowElm.onSelectSlide(index);
      }
    }
  }

  protected renderEditSelectPages(_item: any): TemplateResult<1> {
    return html``;
  }

  protected async onBackup() {}

  protected viewFields(): SectionField[] {
    return [];
  }

  protected lists(): ListDefinition[] {
    return [];
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return this.entityView;
  }

  private get statusText() {
    const status = this.entityView.orderStatus;
    if (status === 'connected') {
      if (!this.entityView.playlist.length) {
        return t('screens.statusConnectedNoPresentation');
      }
      return t('screens.statusActive');
    }
    if (status === 'ordered') {
      if (!this.entityView.playlist.length) {
        return t('screens.statusOrderedNoPresentation');
      }
      return t('screens.statusOrdered');
    }
    return t('screens.statusNotConnected');
  }

  private renderEmptyInfo(mode: 'view' | 'edit') {
    return html`
      <div class="status">
        <d-wrap split tight>
          <d-view-text
            inline-label
            system-content
            label="Trinnvis Infoskjerm"
            .value=${t('screens.emptyInfoText')}
          ></d-view-text>
          <div>
            ${mode === 'view'
              ? html`<d-action plain @click=${() => this.onEditModeOn()}>${t('screens.createPresentation')}</d-action>`
              : nothing}
            <d-action plain @click=${() => this.handleOrder()}>${t('screens.readMore')}</d-action>
          </div>
        </d-wrap>
      </div>
    `;
  }

  private renderStatusAction() {
    if (this.entityView.orderStatus === 'none') {
      return html`<d-action plain @click=${() => this.handleOrder()}>${t('screens.connect')}</d-action>`;
    }
    if (!this.entityView.playlist.length) {
      return html`<d-action plain @click=${() => this.onEditModeOn()}>${t('screens.createPresentation')}</d-action>`;
    }
    return nothing;
  }

  private renderOrderStatus() {
    if (this.entityView.orderStatus === 'none' && !this.entityView.playlist.length) {
      return html` ${this.renderEmptyInfo('view')} `;
    }
    return html`
      <div class="status">
        <d-wrap split tight baseline>
          <d-view-text
            inline-label
            system-content
            label=${t('screens.connectionLabel')}
            .value=${this.statusText}
          ></d-view-text>
          <div>${this.renderStatusAction()}</div>
        </d-wrap>
      </div>
    `;
  }

  /**
   * Fjernstyringsknapper (#13390). Vises kun når skjermen er tilkoblet en
   * spiller — det er ingen boks å styre før den er levert/paret. Knappene låses
   * samlet mens et kall pågår (det blokkerende endepunktet kan ta noen sekunder).
   */
  private renderRemoteControl() {
    if (this.entityView.orderStatus !== 'connected') {
      return nothing;
    }
    const busy = this.commandInFlight !== null;
    return html`
      <div class="remote-control">
        <d-action plain ?disabled=${busy} @click=${() => this.sendCommand('screen-on')}>
          ${t('screens.remoteControl.on')}
        </d-action>
        <d-action plain ?disabled=${busy} @click=${() => this.sendCommand('screen-off')}>
          ${t('screens.remoteControl.off')}
        </d-action>
        <d-action plain ?disabled=${busy} @click=${() => this.sendCommand('reboot')}>
          ${t('screens.remoteControl.restart')}
        </d-action>
      </div>
    `;
  }

  /**
   * Sender en kommando og gir tilbakemelding via slide-notification. Vi lover
   * aldri at handlingen er *utført* — bare at boksen mottok kommandoen
   * (`delivered`). Reboot/CEC kan ikke bekreftes. Starter med 6 s ventetid.
   */
  private async sendCommand(command: ScreenCommand) {
    if (this.commandInFlight) return;
    this.commandInFlight = command;
    try {
      // waitSeconds utelates med vilje – default (6 s) ligger i sendScreenCommand.
      const result = await sendScreenCommand(
        Number(this.entityView.organizationId),
        Number(this.entityView.uuid),
        command,
      );
      this.notify({ primaryText: this.commandResultText(result) });
    } catch (err) {
      console.warn('[signage] sendScreenCommand failed:', err);
      this.notify({ primaryText: t('screens.remoteControl.error') });
    } finally {
      this.commandInFlight = null;
    }
  }

  private commandResultText(result: ScreenCommandResult): string {
    if (result.delivered) {
      return t('screens.remoteControl.sent');
    }
    if (result.screenOnline === false) {
      return t('screens.remoteControl.offline');
    }
    return t('screens.remoteControl.notDelivered');
  }

  private notify(content: SlideNotificationContent) {
    this.dispatchEvent(
      new CustomEvent<SlideNotificationContent>('slide-notification', {
        bubbles: true,
        composed: true,
        detail: content,
      }),
    );
  }

  private async handleOrder() {
    const promoData = promo.find((p) => p.id === 'screen-order');
    if (!promoData) {
      return;
    }
    const result: PromoDialogResult = await DPromoDialog.open({
      promoData,
      features: features,
      employeesCount: this.entityView.employeesCount,
      specialTerms: this.entityView.specialTerms,
      healthcareSignatoryOptions: [],
      healthcareSignatory: undefined,
      canUpgrade: this.entityView.currentUserHasWriteAccess,
      hasHealthcare: this.entityView.featureStates.healthcare,
    });

    if (result.action === 'upgrade') {
      this.dispatchEvent(
        new CustomEvent('place-screen-order', {
          bubbles: true,
          composed: true,
          detail: {
            organizationId: Number(this.entityView.organizationId),
            screenId: Number(this.entityView.uuid),
            currentName: this.entityView.rawName,
            workWeek: this.entityView.schedule,
            playlist: this.entityView.playlist,
          },
        }),
      );
    }
  }

  private onUploadPresentation(e: CustomEvent<{ file: File; name: string; mimeType: string }>) {
    this.dispatchEvent(
      new CustomEvent<ScreenMediaUploadPayload>('add-screen-media', {
        bubbles: true,
        composed: true,
        detail: {
          screenId: Number(this.entityView.uuid),
          organizationId: Number(this.entityView.organizationId),
          name: e.detail.name,
          mimeType: e.detail.mimeType,
          file: e.detail.file,
        },
      }),
    );
  }

  private onAddNrkNyheter() {
    displayAlert(t('screens.nrkNewsNotImplemented'));
  }

  private onAddYrVaermelding() {
    displayAlert(t('screens.yrWeatherNotImplemented'));
  }

  private editFields(): SectionField[] {
    return [
      {
        field: 'name',
        type: 'edit-text',
        class: 'fullWidth',
        placeholder: t('collections.fieldMustBeFilledOut'),
        markIfEmpty: true,
      },
    ];
  }

  private onWorkWeeksChanged(e: CustomEvent<{ workWeeks: WorkScheduleWeek[] }>) {
    e.stopPropagation();
    if (this.editItem !== undefined) {
      this.editItem = {
        ...this.editItem,
        workWeek: e.detail.workWeeks[0],
      };
    }
  }

  /**
   * Trekker UUID-tokenet ut av entityView.previewUrl. Verdien er en path
   * som `/signage/preview/{token}`; tokenet brukes som path-parameter på
   * draft-endepunktene.
   */
  private get previewToken(): string {
    const url = this.entityView.previewUrl ?? '';
    const m = url.match(/\/preview\/([^/?]+)/);
    return m ? m[1] : '';
  }

  /**
   * Bygger draft-payloaden serveren forventer. Lit bygger nye editItem-
   * referanser ved hver mutasjon, så denne kalles fra `updated()` med
   * fersk state.
   */
  private buildDraftSettings(item: ScreenViewEditItem, token: string): unknown {
    return {
      screenId: Number(this.entityView.uuid),
      organizationId: Number(this.entityView.organizationId),
      name: item.name,
      previewToken: token,
      playlist: item.playlist,
      workWeek: item.workWeek,
      updatedAt: new Date().toISOString(),
    };
  }

  /**
   * Pusher gjeldende editItem til iframen via postMessage (umiddelbart)
   * og til serverens draft-lokasjon (debouncet) — så preview reflekterer
   * pågående redigering uten at brukeren må trykke «Ferdig».
   */
  private pushDraft() {
    const token = this.previewToken;
    if (!token || !this.editItem) return;
    const settings = this.buildDraftSettings(this.editItem, token);
    this.previewIframe?.contentWindow?.postMessage({ type: 'preview-state', settings }, PREVIEW_API_ORIGIN);
    if (this.draftDebounceTimer) clearTimeout(this.draftDebounceTimer);
    this.draftDebounceTimer = setTimeout(() => {
      updateScreenDraft(token, settings).catch((err) => {
        console.warn('[live-preview] draft update failed:', err);
      });
    }, DRAFT_DEBOUNCE_MS);
  }

  override updated(changedProperties: PropertyValues) {
    super.updated?.(changedProperties);
    if (changedProperties.has('editItem') && this.editItem && this.mode === 'edit') {
      this.pushDraft();
    }
  }

  override onEditModeOff() {
    // Avbryt-flow: rydd draft før vanlig edit-off så preview faller
    // tilbake til lagret tilstand. Save-flow er rydder backend selv.
    const token = this.previewToken;
    if (token) {
      deleteScreenDraft(token).catch((err) => {
        console.warn('[live-preview] draft delete failed:', err);
      });
    }
    if (this.draftDebounceTimer) {
      clearTimeout(this.draftDebounceTimer);
      this.draftDebounceTimer = null;
    }
    super.onEditModeOff();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-screen-view': DScreenView;
  }
}
